import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@hero/hooks-shared';
import type { Agreement, Id } from '@hero/schema';
import { useAgreementEditManager } from '@hero/web-hooks';
import type { AgreementCreateDefaults } from '@hero/web-hooks';
import { Button } from '../../../ui/Button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../ui/Sheet';
import { AgreementForm, type AgreementFormDisplayFor } from '../Form/AgreementForm';

/**
 * Display context for the agreement create/edit surface. Forwarded
 * straight through to {@link AgreementForm} so the chip-rendering
 * logic lives in one place. See `AgreementForm.tsx` for the per-
 * field decoration rules.
 */
export type AgreementEditDrawerDisplayFor = AgreementFormDisplayFor;

export interface AgreementEditDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * When set, load this agreement and edit; when undefined, create
   * mode. Mirrors the `ContactEditModal({ contactId })` /
   * `LocationEditModal({ savedLocationId })` pattern — IDs are the
   * source of truth, the drawer loads the entity itself.
   */
  agreementId?: Id;
  /**
   * Pre-fill counterparty (create mode only) — e.g. opening from a
   * Client workspace seeds the picker with that Client. The
   * manager maps these into the form-values shape; the drawer
   * stays unaware of the `parties[]` / `Party` schema.
   */
  defaults?: AgreementCreateDefaults;
  /** Lock specific fields (currently only `counterparty`). */
  lockedFields?: Array<'counterparty'>;
  /**
   * Visual context for the embedded form. Defaults to `'crm'`.
   * Pass `'financing'` from the apply-flow's requirement card so
   * the contract upload renders the red `Required for financing`
   * chip.
   */
  displayFor?: AgreementEditDrawerDisplayFor;
  /** Fired with the saved Agreement after a successful create / update. */
  onAgreementSaved?: (agreement: Agreement) => void;
}

/**
 * `AgreementEditDrawer` — Create / edit form for {@link Agreement},
 * hosted in a side-`Sheet` drawer.
 *
 * This is the single home for agreement create/edit across the
 * platform. It replaces the previous `AgreementEditModal` and
 * the financing-flow-specific `AgreementCreateDrawer` — both used
 * the same {@link useAgreementEditManager} + {@link AgreementForm}
 * pair, just hosted in different chrome. Consolidating onto a
 * drawer means:
 *
 * - the surface matches the other financing requirement drawers
 *   (carrier legal, banking, client legal) for visual consistency,
 * - there is only one host to maintain (reset semantics, success
 *   auto-close, save-button label, etc.), and
 * - the `displayFor` knob makes context-specific field decoration
 *   a one-line change at the call site.
 *
 * The drawer stays thin: validation, payload assembly, and
 * create-vs-edit branching all live inside the manager hook. The
 * counterparty is auto-locked in edit mode (an existing agreement
 * is bilaterally identified by its tenants; re-aiming it at a
 * different client is "create a new agreement" instead).
 */
export function AgreementEditDrawer({
  isOpen,
  onClose,
  agreementId,
  defaults,
  lockedFields,
  displayFor = 'crm',
  onAgreementSaved,
}: AgreementEditDrawerProps) {
  const { t } = useTranslation(['m-agreement', 'c-main']);
  const { orgId } = useCurrentUser();

  const {
    formHook,
    savedAgreement,
    agreementQuery,
    createApi,
    updateApi,
    reset,
    onSaveClick,
    isEdit,
  } = useAgreementEditManager({
    agreementId,
    orgId,
    defaults,
    onAgreementSaved: agreement => {
      onAgreementSaved?.(agreement);
      onClose();
    },
  });

  // The counterparty is part of the bilateral identity of an
  // agreement (it indexes `tenants[]` and is enforced immutable
  // by `agreementUpdateAPISchema`). Always lock it in edit mode
  // so the user can't try to "re-aim" an existing contract at a
  // different client — that flow is "create a new agreement".
  const effectiveLockedFields = useMemo<Array<'counterparty'>>(() => {
    const base = lockedFields ? [...lockedFields] : [];
    if (isEdit && !base.includes('counterparty')) base.push('counterparty');
    return base;
  }, [lockedFields, isEdit]);

  // Reset on close so re-opening doesn't show a stale success
  // state (which the form watches to drive its auto-close).
  // Depend only on `isOpen` — `reset` identity-change would
  // clobber in-progress edits otherwise.
  useEffect(() => {
    if (!isOpen) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isLoadingForm = isEdit && agreementQuery.isLoading;
  const isLoading = createApi.isPending || updateApi.isPending;

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col overflow-y-auto p-0 sm:max-w-xl">
        <SheetHeader className="sticky top-0 z-10 border-b bg-white px-6 py-4">
          <SheetTitle>
            {isEdit
              ? t('m-agreement:modal.edit_agreement_title')
              : t('m-agreement:modal.create_agreement_title')}
          </SheetTitle>
          <SheetDescription>
            {isEdit
              ? t('m-agreement:modal.edit_agreement_description')
              : t('m-agreement:modal.create_agreement_description')}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 p-6">
          {isLoadingForm ? (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">
              {t('c-main:async_operations.loading')}
            </div>
          ) : (
            <AgreementForm
              formHook={formHook}
              agreement={savedAgreement}
              lockedFields={effectiveLockedFields}
              displayFor={displayFor}
            />
          )}
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t bg-white px-6 py-3">
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            {t('c-main:crud_buttons.cancel')}
          </Button>
          <Button onClick={onSaveClick} disabled={!formHook.isValid || isLoading}>
            {isLoading
              ? t('c-main:async_operations.saving')
              : isEdit
                ? t('c-main:crud_buttons.update')
                : t('c-main:crud_buttons.create')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
